import { List } from '@gonfalon/immutable-shim/immutableShim';

import { InstructionCategory } from '#utils/instructions/shared/constants';
import type { InstructionsType, SemanticInstruction } from '#utils/instructions/shared/types';

import type {
  AddUserTargetsSemanticInstruction,
  RemoveUserTargetsSemanticInstruction,
  ReplaceUserTargetsSemanticInstruction,
  UpdateUserTargetsSemanticInstruction,
} from './types';
import { UserTargetsInstructionKind } from './types';

export function makeUpdateUserTargetsKey(variationId: string, instructionKind: UserTargetsInstructionKind): string {
  return [InstructionCategory.USER_TARGETS, variationId, instructionKind].join('|');
}

export function makeAddUserTargetsInstruction(
  userKeys: string[],
  variationId: string,
): AddUserTargetsSemanticInstruction {
  return {
    kind: UserTargetsInstructionKind.ADD_USER_TARGETS,
    values: userKeys,
    variationId,
  };
}

export function makeRemoveUserTargetsInstruction(
  userKeys: string[],
  variationId: string,
): RemoveUserTargetsSemanticInstruction {
  return {
    kind: UserTargetsInstructionKind.REMOVE_USER_TARGETS,
    values: userKeys,
    variationId,
  };
}

// sortUserTargetInstructions puts all of the remove user target instructions before all of the
// add user target instructions. This ensures that if a user is moving from one variation to another,
// there won't be an error where we try to add the user before removing it from the current variation.
export function sortUserTargetInstructions(
  instructions: List<SemanticInstruction> | undefined,
): List<SemanticInstruction> {
  if (!instructions) {
    return List();
  }
  const removeUserTargets = instructions.filter((i) => i.kind === UserTargetsInstructionKind.REMOVE_USER_TARGETS);
  const addUserTargets = instructions.filter((i) => i.kind === UserTargetsInstructionKind.ADD_USER_TARGETS);
  return removeUserTargets.concat(addUserTargets);
}

/**
 * Sets the add or remove user target instruction for this variationId.
 * Net add/remove lists are already computed by getInstructionsForTargets (user targets), so we only store the instruction.
 */
export function combineUserTargetsInstructions(
  newInstruction: UpdateUserTargetsSemanticInstruction,
  pendingSemanticPatch: InstructionsType,
): InstructionsType {
  const key = makeUpdateUserTargetsKey(newInstruction.variationId, newInstruction.kind);
  return pendingSemanticPatch.set(key, newInstruction);
}

export function makeReplaceUserTargetsInstruction(
  targets: Array<{ variationId: string; values: string[] }>,
): ReplaceUserTargetsSemanticInstruction {
  return {
    kind: UserTargetsInstructionKind.REPLACE_USER_TARGETS,
    targets,
  };
}

export function hasEmptyUserTargets(instruction: ReplaceUserTargetsSemanticInstruction) {
  return instruction.targets.every((v) => v.values.length === 0);
}
